/**
 * Lightweight phase profiler for the pre-content-paint boot path. Each gate
 * (PersistGate, AuthWrapper, Auth0, useCurrentUser, translation fetch,
 * AuthGuard, UserLayout, etc.) calls `bootMark(phase)` when it releases. Once
 * the final gate fires, `bootReport()` prints a console.table with each
 * phase's offset from page-navigation start plus the delta from the previous
 * phase — making it obvious which gate is the actual blocker.
 *
 * Always-on (the cost is one `performance.mark` per phase), but the
 * console.table only prints when `localStorage.bootProfile === 'true'`.
 * Enable in any environment by running this in DevTools:
 *
 *   localStorage.setItem('bootProfile', 'true')
 *
 * and disable with `localStorage.removeItem('bootProfile')`.
 */

import { sortBy } from 'lodash'
import { useEffect } from 'react'

import { Logger } from './logger'

const bootLogger = new Logger('boot')
const recorded = new Set<string>()
let reportScheduled = false

function shouldPrint(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem('bootProfile') === 'true'
  } catch {
    return false
  }
}

export function bootMark(phase: string): void {
  if (typeof performance === 'undefined') return
  // Only record the first occurrence — gates can re-fire on re-renders and
  // we want phase boundaries, not noise.
  if (recorded.has(phase)) return
  try {
    performance.mark(`boot:${phase}`)
    // Only record AFTER a successful mark — if performance.mark throws (some
    // shims do on duplicate names), leave the phase unrecorded so a later
    // attempt can still succeed.
    recorded.add(phase)
  } catch {
    // performance.mark can throw on duplicate names in some shims — ignore.
  }
}

export function bootReport(trigger: string): void {
  if (typeof performance === 'undefined') return
  if (!shouldPrint()) return
  if (reportScheduled) return
  reportScheduled = true

  // Wait a tick past the trigger so any same-frame marks land before we read.
  setTimeout(() => {
    // Pick up every "boot:*" mark, including ones made directly via
    // performance.mark() by callers that don't import bootMark (e.g. an
    // inline boot script in the document head).
    const entries = sortBy(
      performance
        .getEntriesByType('mark')
        .filter((m) => m.name.startsWith('boot:'))
        .map((m) => ({ phase: m.name.slice('boot:'.length), t: m.startTime })),
      't'
    )
    if (entries.length === 0) {
      // No marks have been recorded yet — release the lock so a later call
      // (e.g. once gates start firing) can produce a real report instead of
      // being permanently suppressed.
      reportScheduled = false
      return
    }

    const rows = entries.map((e, i) => ({
      phase: e.phase,
      'since nav (ms)': Math.round(e.t),
      'delta (ms)': i === 0 ? 0 : Math.round(e.t - entries[i - 1]!.t)
    }))

    const total = entries[entries.length - 1]!.t
    bootLogger.table(rows, `${trigger} reached at ${Math.round(total)}ms`)
  }, 50)
}

type BootMarkOptions = {
  phase: string
  when?: boolean
  report?: boolean
}

/**
 * Idiomatic call-site for instrumenting a gate. Wraps the bootMark/bootReport
 * pair in a single useEffect so it fires once a render has actually committed
 * (instead of during render, where StrictMode dry-runs and aborted renders
 * would record misleading timestamps). Call sites become a single line:
 *
 *   useBootMark({ phase: 'current-user-resolved', when: !!dbUser })
 *
 * Pass `report: true` to also fire `bootReport` once `when` flips true,
 * useful for the final mark in the boot path (typically first-content-paint).
 */
export function useBootMark({ phase, when = true, report }: BootMarkOptions): void {
  useEffect(() => {
    if (!when) return
    bootMark(phase)
    if (report) bootReport(phase)
  }, [phase, when, report])
}
